import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const UpcomingAppointments = ({ appointments, onScheduleNew, onViewDetails }) => {
  const getAppointmentTypeColor = (type) => {
    switch (type) {
      case 'primary-care':
        return 'bg-primary/10 text-primary';
      case 'specialist':
        return 'bg-secondary/10 text-secondary';
      case 'follow-up':
        return 'bg-accent/10 text-accent';
      default:
        return 'bg-muted text-muted-foreground';
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date?.toLocaleDateString('en-US', { 
      weekday: 'short', 
      month: 'short', 
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatTime = (timeString) => {
    const [hours, minutes] = timeString?.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  return (
    <div className="bg-card rounded-xl border border-border p-4 md:p-6">
      <div className="flex items-center justify-between mb-4 md:mb-6">
        <div className="flex items-center gap-3">
          <div className="flex items-center justify-center w-10 h-10 md:w-12 md:h-12 bg-primary/10 rounded-lg">
            <Icon name="Calendar" size={20} color="var(--color-primary)" />
          </div>
          <div>
            <h2 className="text-lg md:text-xl font-semibold text-foreground">Upcoming Appointments</h2>
            <p className="text-xs md:text-sm text-muted-foreground">Your scheduled visits</p>
          </div>
        </div>
        <Button
          variant="outline"
          size="sm"
          iconName="Plus"
          iconPosition="left"
          onClick={onScheduleNew}
          className="hidden md:flex"
        >
          Schedule New
        </Button>
        <Button
          variant="outline"
          size="icon"
          onClick={onScheduleNew}
          className="md:hidden"
        >
          <Icon name="Plus" size={18} />
        </Button>
      </div>
      <div className="space-y-3 md:space-y-4">
        {appointments?.length === 0 ? (
          <div className="text-center py-8 md:py-12">
            <div className="flex items-center justify-center w-16 h-16 md:w-20 md:h-20 bg-muted rounded-full mx-auto mb-4">
              <Icon name="CalendarX" size={32} className="text-muted-foreground" />
            </div>
            <p className="text-sm md:text-base text-muted-foreground mb-4">No upcoming appointments</p>
            <Button variant="default" onClick={onScheduleNew} iconName="Plus" iconPosition="left">
              Schedule Your First Appointment
            </Button>
          </div>
        ) : (
          appointments?.map((appointment) => (
            <div
              key={appointment?.id}
              className="bg-background rounded-lg border border-border p-4 transition-smooth hover:shadow-elevation-2"
            >
              <div className="flex flex-col md:flex-row md:items-center gap-4">
                <div className="flex items-center gap-4 flex-1 min-w-0">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 md:w-14 md:h-14 bg-primary/10 rounded-lg flex items-center justify-center">
                      <Icon name="Stethoscope" size={24} color="var(--color-primary)" />
                    </div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2 mb-1">
                      <h3 className="text-sm md:text-base font-semibold text-foreground truncate">
                        {appointment?.providerName}
                      </h3>
                      <span className={`text-xs font-medium px-2 py-1 rounded ${getAppointmentTypeColor(appointment?.type)}`}>
                        {appointment?.typeLabel}
                      </span>
                    </div>
                    <p className="text-xs md:text-sm text-muted-foreground mb-2">{appointment?.specialty}</p>
                    <div className="flex flex-wrap items-center gap-3 md:gap-4 text-xs md:text-sm text-muted-foreground">
                      <div className="flex items-center gap-1.5">
                        <Icon name="Calendar" size={14} />
                        <span>{formatDate(appointment?.date)}</span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <Icon name="Clock" size={14} />
                        <span>{formatTime(appointment?.time)}</span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <Icon name="MapPin" size={14} />
                        <span className="truncate">{appointment?.location}</span>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2 md:flex-col md:items-end">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => onViewDetails(appointment)}
                    className="flex-1 md:flex-none"
                  >
                    View Details
                  </Button>
                  {appointment?.telehealth && (
                    <Button
                      variant="default"
                      size="sm"
                      iconName="Video"
                      iconPosition="left"
                      className="flex-1 md:flex-none"
                    >
                      Join Video
                    </Button>
                  )}
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default UpcomingAppointments;